import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ShopListItem, ShopProduct, Thumbnail } from './api';

/**
 * Naposledy prohlížené zboží.
 *
 * Žije v prohlížeči, stejně jako košík a oblíbené – žádné přihlášení. Úloha
 * je „vrať se, kde jsi byl", ne „drž mi to za cenu": ukládá se jen to, co
 * dlaždice potřebuje k zobrazení, živá data se vyzvednou až na detailu.
 */

const STORAGE_KEY = 'zooshopik.recentlyViewed.v1';
const LIMIT = 12;

interface RecentlyViewedState {
  readonly items: readonly ShopListItem[];
  record(item: ShopListItem): void;
}

const RecentlyViewedContext = createContext<RecentlyViewedState | null>(null);

/** Z detailu produktu složí dlaždicovou položku pro řadu „naposledy". */
export function productToListItem(product: ShopProduct): ShopListItem {
  const prices = product.variants.map((v) => v.priceMinor);
  // compareAt jako MAX přes varianty – shodně s SQL projekcí katalogu
  // (max(compare_at_price_minor)), ať stejný produkt ukazuje stejnou
  // přeškrtnutou cenu i slevu ve všech řadách, kde sdílí ProductCard.
  const compareAts = product.variants
    .map((v) => v.compareAtPriceMinor)
    .filter((c): c is number => c !== null);
  const single = product.variants.length === 1 ? product.variants[0] : undefined;
  const image = product.images[0];

  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    brand: product.brand,
    priceFromMinor: prices.length > 0 ? Math.min(...prices) : 0,
    priceToMinor: prices.length > 0 ? Math.max(...prices) : 0,
    compareAtPriceFromMinor: compareAts.length > 0 ? Math.max(...compareAts) : null,
    inStock: product.variants.some((v) => v.available > 0),
    imageUrl: image?.url ?? null,
    imageThumbnails: image?.thumbnails ?? [],
    variantCount: product.variants.length,
    singleVariantId: single?.id ?? null,
    singleVariantName: single?.name ?? null,
    singleVariantAvailable: single ? Math.max(0, single.available) : 0,
    ratingAverage: product.ratingAverage,
    ratingCount: product.ratingCount,
    createdAt: product.createdAt,
  };
}

/**
 * Data z localStorage může upravit kdokoli, proto každou položku
 * NORMALIZUJEME na kompletní tvar – chybějící nebo rozbité pole dostane
 * bezpečnou výchozí hodnotu, jinak by dlaždice spadla na chybějícím obrázku
 * nebo ceně. Co nemá ani id a slug, se zahodí.
 */
function readStorage(): ShopListItem[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const out: ShopListItem[] = [];
    for (const entry of parsed) {
      if (typeof entry !== 'object' || entry === null) continue;
      const item = entry as Record<string, unknown>;
      if (typeof item['id'] !== 'string' || typeof item['slug'] !== 'string') continue;
      out.push({
        id: item['id'],
        slug: item['slug'],
        name: typeof item['name'] === 'string' ? item['name'] : '',
        brand: typeof item['brand'] === 'string' ? item['brand'] : null,
        priceFromMinor: typeof item['priceFromMinor'] === 'number' ? item['priceFromMinor'] : 0,
        priceToMinor: typeof item['priceToMinor'] === 'number' ? item['priceToMinor'] : 0,
        compareAtPriceFromMinor:
          typeof item['compareAtPriceFromMinor'] === 'number' ? item['compareAtPriceFromMinor'] : null,
        inStock: item['inStock'] === true,
        imageUrl: typeof item['imageUrl'] === 'string' ? item['imageUrl'] : null,
        imageThumbnails: Array.isArray(item['imageThumbnails'])
          ? (item['imageThumbnails'] as unknown[]).filter(
              (t): t is Thumbnail =>
                typeof t === 'object' &&
                t !== null &&
                typeof (t as Record<string, unknown>)['url'] === 'string' &&
                typeof (t as Record<string, unknown>)['width'] === 'number',
            )
          : [],
        variantCount: typeof item['variantCount'] === 'number' ? item['variantCount'] : 1,
        singleVariantId: typeof item['singleVariantId'] === 'string' ? item['singleVariantId'] : null,
        singleVariantName:
          typeof item['singleVariantName'] === 'string' ? item['singleVariantName'] : null,
        singleVariantAvailable:
          typeof item['singleVariantAvailable'] === 'number' ? item['singleVariantAvailable'] : 0,
        ratingAverage: typeof item['ratingAverage'] === 'number' ? item['ratingAverage'] : 0,
        ratingCount: typeof item['ratingCount'] === 'number' ? item['ratingCount'] : 0,
        createdAt: typeof item['createdAt'] === 'string' ? item['createdAt'] : '',
      });
      if (out.length >= LIMIT) break;
    }
    return out;
  } catch {
    return [];
  }
}

export function RecentlyViewedProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ShopListItem[]>(readStorage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Privátní režim nebo plné úložiště – historie zůstane aspoň v paměti.
    }
  }, [items]);

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === STORAGE_KEY) setItems(readStorage());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const record = useCallback((item: ShopListItem) => {
    setItems((current) => [item, ...current.filter((existing) => existing.id !== item.id)].slice(0, LIMIT));
  }, []);

  const value = useMemo<RecentlyViewedState>(() => ({ items, record }), [items, record]);

  return <RecentlyViewedContext.Provider value={value}>{children}</RecentlyViewedContext.Provider>;
}

export function useRecentlyViewed(): RecentlyViewedState {
  const context = useContext(RecentlyViewedContext);
  if (context === null) throw new Error('useRecentlyViewed musí být uvnitř RecentlyViewedProvider.');
  return context;
}
